"use client";

import {
  Button,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowDownIcon, PlusIcon, XIcon } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { formatDateTime } from "@/shared/lib/date-time";
import { formatPrice } from "@/shared/lib/format-price";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import type { RefundItem } from "../api/refund-client";
import { useRefundListQuery } from "../api/refund-queries";

const STATUS_OPTIONS = [
  { value: "all", label: "ສະຖານະທັງໝົດ" },
  { value: "pending", label: "ລໍຖ້າ" },
  { value: "posted", label: "ສຳເລັດ" },
  { value: "failed", label: "ລົ້ມເຫຼວ" },
  { value: "reversed", label: "ກັບຄືນ" },
] as const;

const REFERENCE_TYPE_OPTIONS = [
  { value: "all", label: "ອ້າງອີງທັງໝົດ" },
  { value: "order", label: "ຄຳສັ່ງຊື້" },
  { value: "job", label: "ງານ" },
  { value: "manual", label: "ແມນນວນ" },
] as const;

const STATUS_STYLE: Record<
  string,
  { dot: string; text: string; label: string }
> = {
  pending: {
    dot: "bg-primary",
    text: "text-primary",
    label: "ລໍຖ້າ",
  },
  posted: {
    dot: "bg-success",
    text: "text-success",
    label: "ສຳເລັດ",
  },
  failed: {
    dot: "bg-destructive",
    text: "text-destructive",
    label: "ລົ້ມເຫຼວ",
  },
  reversed: {
    dot: "bg-muted-foreground",
    text: "text-muted-foreground",
    label: "ກັບຄືນ",
  },
};

const REF_LABELS: Record<string, string> = {
  order: "ຄຳສັ່ງຊື້",
  job: "ງານ",
  manual: "ແມນນວນ",
};

const OWNER_TYPE_LABELS: Record<string, string> = {
  customer: "ລູກຄ້າ",
  shop: "ຮ້ານຄ້າ",
  mechanic: "ຊ່າງ",
  platform: "ແພລດຟອມ",
};

export function RefundsPage() {
  const nav = useNavigate({ from: "/app/wallet/refunds" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/wallet/refunds",
  });

  const list = useRefundListQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];
  const statusFilter = findCondition(filters, "status");
  const refTypeFilter = findCondition(filters, "referenceType");

  const statusValue = (statusFilter?.value as string) || "all";
  const refTypeValue = (refTypeFilter?.value as string) || "all";
  const hasFilter = statusValue !== "all" || refTypeValue !== "all";

  function handleFilterChange(field: string, value: string) {
    let next: FilterConditionDTO[] | undefined = filters;
    if (value === "all") {
      next = removeConditions(filters, field);
    } else {
      next = upsertCondition(filters, { field, op: "eq", value });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next?.length ? next : undefined,
      },
    });
  }

  const columns: TanstackReactTable.ColumnDef<RefundItem>[] = [
    createSortableColumn<RefundItem>("createdAt", "ວັນທີ", {
      size: 170,
      cell: ({ row }) => (
        <span className="text-sm tabular-nums">
          {formatDateTime(row.original.createdAt)}
        </span>
      ),
    }),
    {
      id: "amount",
      header: "ຈຳນວນເງິນ",
      cell: ({ row }) => {
        const tx = row.original;
        return (
          <div className="flex items-center gap-1.5">
            <ArrowDownIcon className="size-4 text-success" />
            <span className="font-semibold text-success tabular-nums">
              +{formatPrice(tx.amount, tx.currency)}
            </span>
          </div>
        );
      },
      size: 180,
    },
    {
      id: "balanceAfter",
      header: "ຍອດຄົງເຫຼືອ",
      cell: ({ row }) => {
        const ba = row.original.balanceAfter;
        if (ba == null) return <span className="text-muted-foreground">—</span>;
        return (
          <span className="text-sm tabular-nums">
            {formatPrice(ba, row.original.currency)}
          </span>
        );
      },
      size: 160,
    },
    {
      id: "reference",
      header: "ອ້າງອີງ",
      cell: ({ row }) => {
        const tx = row.original;
        if (!tx.referenceType) {
          return <span className="text-muted-foreground">—</span>;
        }
        return (
          <div className="min-w-0">
            <div className="text-sm">
              {REF_LABELS[tx.referenceType] ?? tx.referenceType}
            </div>
            {tx.referenceId && (
              <div className="truncate text-muted-foreground text-xs">
                #{tx.referenceId.slice(0, 8)}
              </div>
            )}
          </div>
        );
      },
      size: 140,
    },
    {
      id: "owner",
      header: "ເຈົ້າຂອງ",
      cell: ({ row }) => {
        const tx = row.original;
        if (!tx.ownerName) {
          return <span className="text-muted-foreground">—</span>;
        }
        return (
          <div className="min-w-0">
            <div className="truncate font-medium text-sm">{tx.ownerName}</div>
            <div className="text-muted-foreground text-xs">
              {OWNER_TYPE_LABELS[tx.ownerType ?? ""] ?? tx.ownerType}
            </div>
          </div>
        );
      },
      size: 160,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => {
        const s = row.original.status;
        const style = STATUS_STYLE[s] ?? {
          dot: "bg-muted-foreground",
          text: "text-muted-foreground",
          label: s,
        };
        return (
          <div className="flex items-center gap-2">
            <span
              className={`size-2 shrink-0 rounded-full ${style.dot}`}
              aria-hidden
            />
            <span className={`text-sm ${style.text}`}>{style.label}</span>
          </div>
        );
      },
      size: 120,
    },
  ];

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ກະເປົາເງິນ & ການຈ່າຍເງິນ", to: "/app/wallet" },
            { label: "ຄືນເງິນ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-2 flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="font-bold text-2xl tracking-tight">ຄືນເງິນ</h2>
              <p className="text-muted-foreground">
                ລາຍການຄືນເງິນເຂົ້າກະເປົາ ແລະ ສ້າງຄືນເງິນ (order/job)
              </p>
            </div>
            <Button
              onClick={() => nav({ to: "/app/wallet/refunds/create" })}
              className="shrink-0"
            >
              <PlusIcon className="size-4" /> ສ້າງຄືນເງິນ
            </Button>
          </div>
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <div className="mb-4 flex flex-col gap-2 px-2">
            <div className="flex items-center justify-between gap-4 max-sm:flex-col">
              <div className="flex flex-1 flex-wrap items-center gap-2">
                <SimpleSelect
                  value={statusValue}
                  onValueChange={(val) => handleFilterChange("status", val)}
                  placeholder="ສະຖານະ"
                  className="h-8 w-full sm:w-44"
                  options={STATUS_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                />
                <SimpleSelect
                  value={refTypeValue}
                  onValueChange={(val) =>
                    handleFilterChange("referenceType", val)
                  }
                  placeholder="ປະເພດອ້າງອີງ"
                  className="h-8 w-full sm:w-40"
                  options={REFERENCE_TYPE_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                />
              </div>
              {hasFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    nav({
                      search: { ...search, filters: undefined, offset: 0 },
                    })
                  }
                  className="max-sm:w-full"
                >
                  <XIcon className="size-4" /> ລ້າງ
                </Button>
              )}
            </div>
          </div>

          <DataTable<RefundItem, unknown>
            noDataMessage="ບໍ່ພົບລາຍການຄືນເງິນ"
            isLoading={list.isLoading}
            columns={columns}
            data={list.data?.data ?? []}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(p) =>
              nav({ search: { ...search, offset: p.offset, limit: p.limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(sorting) => {
              if (sorting[0]?.id === "") return;
              nav({
                search: {
                  ...search,
                  sort: [
                    {
                      field: sorting[0]?.id as string,
                      dir: sorting[0]?.desc ? "desc" : "asc",
                    },
                  ],
                },
              });
            }}
          />
        </div>
      </Main>
    </>
  );
}
